'use client';

import { useEffect, useMemo, useState } from 'react';
import { Button, message } from '@okee-uikit/react';
import ky from 'ky';
import { usePathname, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { Markdown } from './markdown';
import { McpInstallWarningBanner } from './mcp-install-warning-banner';
import {
  mapAiSkillHubSkillDownload,
  normalizeCdnUrl,
  type SkillDetailItem,
  type SkillItem,
} from './mock-data';

import s from './index.module.scss';

import { BASE_PATH, LOGIN_PAGE } from '#/utils/const';
import { APIs } from '#/utils/http/api-paths';
import type { AiSkillHubSkillDownloadData, MAxiosResponse } from '#/utils/http/types';

type DetailTabKey = 'details' | 'version-history';

function formatSectionValue(items: string[]) {
  return items.length > 0 ? items.join(', ') : '—';
}

function splitPromptExample(examplePrompt: string) {
  const normalizedPrompt = examplePrompt.trim();

  if (!normalizedPrompt) {
    return {
      prompt: '',
      outcome: '',
    };
  }

  const urlRanges: Array<[number, number]> = [];
  const urlRegex = /https?:\/\/[^\s)]+/gi;
  let match: null | RegExpExecArray = null;

  while ((match = urlRegex.exec(normalizedPrompt)) !== null) {
    urlRanges.push([match.index, match.index + match[0].length]);
  }

  const isInUrlRange = (index: number) =>
    urlRanges.some(([start, end]) => index >= start && index < end);

  const isLikelyUrlBoundaryColon = (index: number) => {
    if (!isInUrlRange(index)) {
      return false;
    }

    const rightText = normalizedPrompt.slice(index + 1);
    const nextWhitespaceIndex = rightText.search(/\s/);
    const rawToken =
      nextWhitespaceIndex < 0 ? rightText.trim() : rightText.slice(0, nextWhitespaceIndex).trim();

    if (!rawToken) {
      return true;
    }

    const semanticToken = rawToken.replace(/^[*_'"([{<]+/, '').replace(/[*_'")\]}>.,!?;:]+$/, '');

    if (!semanticToken) {
      return true;
    }

    const containsUrlSyntax = /[/?&=#%]/.test(rawToken);
    if (containsUrlSyntax) {
      return false;
    }

    if (/^[0-9]/.test(semanticToken)) {
      return true;
    }

    if (/^[A-Z]/.test(semanticToken)) {
      return true;
    }

    return /^[A-Za-z一-鿿_-]{1,40}$/.test(semanticToken);
  };

  const isLikelyUppercaseBoundaryColon = (index: number) => {
    const rightText = normalizedPrompt.slice(index + 1);

    if (!rightText || /^\s/.test(rightText)) {
      return false;
    }

    const nextWhitespaceIndex = rightText.search(/\s/);
    const rawToken = nextWhitespaceIndex < 0 ? rightText : rightText.slice(0, nextWhitespaceIndex);

    if (!rawToken) {
      return false;
    }

    const semanticToken = rawToken.replace(/^[*_"'([{<]+/, '').replace(/[*_'")\]}>.,!?;:]+$/, '');

    if (!semanticToken) {
      return false;
    }

    const containsUrlSyntax = /[/?&=#%]/.test(rawToken);
    if (containsUrlSyntax) {
      return false;
    }

    return /^[0-9A-Z]/.test(semanticToken);
  };

  const colonIndexes: number[] = [];
  for (let index = 0; index < normalizedPrompt.length; index += 1) {
    const char = normalizedPrompt[index];
    if (
      (char === ':' || char === '：') &&
      (!isInUrlRange(index) ||
        isLikelyUrlBoundaryColon(index) ||
        isLikelyUppercaseBoundaryColon(index))
    ) {
      colonIndexes.push(index);
    }
  }

  if (colonIndexes.length < 1) {
    return {
      prompt: normalizedPrompt,
      outcome: '',
    };
  }

  const punctuationBeforeColonIndexes = colonIndexes.filter((index) => {
    const leftText = normalizedPrompt.slice(0, index).trimEnd();
    if (!leftText) {
      return false;
    }

    const previousChar = leftText[leftText.length - 1];
    return ['.', '?', '!', '。', '？', '！'].includes(previousChar);
  });

  const pickFirstSplitIndex = (indexes: number[]) => {
    for (let i = 0; i < indexes.length; i += 1) {
      const index = indexes[i];
      if (normalizedPrompt.slice(index + 1).trim()) {
        return index;
      }
    }

    return -1;
  };

  const pickLastSplitIndex = (indexes: number[]) => {
    for (let i = indexes.length - 1; i >= 0; i -= 1) {
      const index = indexes[i];
      if (normalizedPrompt.slice(index + 1).trim()) {
        return index;
      }
    }

    return -1;
  };

  const urlBoundaryColonIndexes = colonIndexes.filter((index) => isInUrlRange(index));
  const uppercaseBoundaryColonIndexes = colonIndexes.filter(
    (index) => !isInUrlRange(index) && isLikelyUppercaseBoundaryColon(index)
  );

  const separatorIndex =
    pickFirstSplitIndex(urlBoundaryColonIndexes) >= 0
      ? pickFirstSplitIndex(urlBoundaryColonIndexes)
      : pickFirstSplitIndex(uppercaseBoundaryColonIndexes) >= 0
        ? pickFirstSplitIndex(uppercaseBoundaryColonIndexes)
        : pickLastSplitIndex(punctuationBeforeColonIndexes) >= 0
          ? pickLastSplitIndex(punctuationBeforeColonIndexes)
          : pickLastSplitIndex(colonIndexes);

  if (separatorIndex < 0) {
    return {
      prompt: normalizedPrompt,
      outcome: '',
    };
  }

  return {
    prompt: normalizedPrompt.slice(0, separatorIndex).trim(),
    outcome: normalizedPrompt.slice(separatorIndex + 1).trim(),
  };
}

function formatVersionDate(value?: string) {
  if (!value) {
    return '—';
  }

  const datePart = value.split('T')[0] || '';
  const [year, month, day] = datePart.split('-');

  if (!year || !month || !day) {
    return value;
  }

  return `${month}/${day}/${year}`;
}

function getVersionHistoryTabItems(t: ReturnType<typeof useTranslations>) {
  return [
    {
      id: 'details',
      title: t('skills_detail_tab_details'),
    },
    {
      id: 'version-history',
      title: t('skills_detail_tab_version_history'),
    },
  ] as const;
}

function DetailTabs({
  activeTab,
  onChange,
}: {
  activeTab: DetailTabKey;
  onChange: (value: DetailTabKey) => void;
}) {
  const t = useTranslations();

  return (
    <div className={s.detailTabs} role='tablist'>
      {getVersionHistoryTabItems(t).map((tab) => (
        <button
          aria-selected={activeTab === tab.id}
          className={activeTab === tab.id ? s.detailTabActive : s.detailTabButton}
          key={tab.id}
          role='tab'
          type='button'
          onClick={() => onChange(tab.id)}
        >
          {tab.title}
        </button>
      ))}
    </div>
  );
}

function DownloadAcknowledge({
  isLoggedIn,
  isTermsAccepted,
  onTermsChange,
}: {
  isLoggedIn: boolean;
  isTermsAccepted: boolean;
  onTermsChange: (checked: boolean) => void;
}) {
  const t = useTranslations();

  if (!isLoggedIn) {
    return null;
  }

  return (
    <div className={s.downloadAcknowledgeInline}>
      <label className={s.downloadAcknowledge}>
        <input
          checked={isTermsAccepted}
          className={s.downloadAcknowledgeCheckbox}
          type='checkbox'
          onChange={(event) => onTermsChange(event.target.checked)}
        />
        <span className={s.downloadAcknowledgeText}>
          {t('skills_download_ack_prefix')}{' '}
          <a
            className={s.downloadAcknowledgeLink}
            href='https://ads.tiktok.com/i18n/official/article?aid=10025123'
            rel='noreferrer'
            target='_blank'
          >
            {t('skills_download_ack_terms')}
          </a>
        </span>
      </label>
    </div>
  );
}

function FooterActions({
  currentUrl,
  detail,
  isDownloading,
  isInstalling,
  isLoggedIn,
  isTermsAccepted,
  onDownload,
  onInstall,
}: {
  currentUrl: string;
  detail: SkillDetailItem | null;
  isDownloading: boolean;
  isInstalling: boolean;
  isLoggedIn: boolean;
  isTermsAccepted: boolean;
  onDownload: () => void;
  onInstall: () => void;
}) {
  const t = useTranslations();

  const loginHref = `${LOGIN_PAGE}?redirect=${encodeURIComponent(currentUrl)}`;

  if (!isLoggedIn) {
    return (
      <div className={s.overlayFooterButtons}>
        <a href={loginHref}>
          <Button shape='round' type='primary'>
            {t('log_in')}
          </Button>
        </a>
      </div>
    );
  }

  const showInstall = detail?.sourceType === 2 && detail.installableIn1PAgent;
  const isDownloadDisabled = !isTermsAccepted || isDownloading || isInstalling;

  return (
    <div className={s.overlayFooterButtons}>
      {showInstall ? (
        <Button
          disabled={isDownloadDisabled}
          loading={isInstalling}
          shape='round'
          type='white'
          onClick={onInstall}
        >
          {t('skills_detail_install')}
        </Button>
      ) : null}
      <Button
        className={s.downloadActionButton}
        disabled={isDownloadDisabled}
        loading={isDownloading}
        shape='round'
        style={
          isDownloadDisabled
            ? {
                backgroundColor: '#C3E9E7',
                borderColor: '#C3E9E7',
                color: '#FFFFFF',
              }
            : undefined
        }
        type='primary'
        onClick={onDownload}
      >
        {t('skills_detail_download')}
      </Button>
    </div>
  );
}

function DetailsTab({
  detail,
  fallbackSkill,
  isLoggedIn,
  isTermsAccepted,
  onTermsChange,
}: {
  detail: SkillDetailItem | null;
  fallbackSkill: SkillItem | null;
  isLoggedIn: boolean;
  isTermsAccepted: boolean;
  onTermsChange: (checked: boolean) => void;
}) {
  const t = useTranslations();
  const promptItems = (detail?.examplePrompts || []).slice(0, 5);
  const shouldShowVideo = Boolean(detail?.screencastVideoLink);
  const detailName = detail?.name || fallbackSkill?.name || '—';
  const detailDescription = detail?.description || fallbackSkill?.description || '—';
  const detailCategories = detail?.categories?.length
    ? detail.categories
    : fallbackSkill?.categories || [];
  const detailCompany = fallbackSkill?.company || '';
  const detailContact = detail?.developerContactEmail || fallbackSkill?.developerContactEmail || '';

  if (!detail) {
    if (!fallbackSkill) {
      return <div className={s.detailEmptyState}>{t('skills_detail_unavailable')}</div>;
    }

    return (
      <div className={s.detailTabContent}>
        <section className={s.detailSection}>
          <span className={s.detailLabel}>{t('skills_column_name')}</span>
          <h3 id='skill-detail-title'>{fallbackSkill.name}</h3>
        </section>

        <section className={s.detailSection}>
          <span className={s.detailLabel}>{t('skills_detail_description_label')}</span>
          {fallbackSkill.description ? (
            <Markdown className={s.richText}>{fallbackSkill.description}</Markdown>
          ) : (
            <p>—</p>
          )}
        </section>

        <div className={s.detailUnavailablePanel}>{t('skills_detail_unavailable')}</div>
      </div>
    );
  }

  return (
    <div className={s.detailTabContent}>
      <section className={s.detailSummaryCard}>
        <div>
          <span className={s.detailLabel}>{t('skills_column_name')}</span>
          <h3 id='skill-detail-title'>{detailName}</h3>
        </div>
        <div className={s.detailSummaryMeta}>
          <div>
            <span className={s.detailLabel}>{t('skills_detail_version_label')}</span>
            <p>{detail.currentVersionSemver || '—'}</p>
          </div>
        </div>
      </section>

      <section className={s.detailSection}>
        <span className={s.detailLabel}>{t('skills_detail_description_label')}</span>
        {detailDescription && detailDescription !== '—' ? (
          <Markdown className={s.richText}>{detailDescription}</Markdown>
        ) : (
          <p>—</p>
        )}
      </section>

      <div className={s.detailInfoGrid}>
        <section className={s.detailSection}>
          <span className={s.detailLabel}>{t('skills_detail_category_label')}</span>
          <p>{formatSectionValue(detailCategories)}</p>
        </section>

        <section className={s.detailSection}>
          <span className={s.detailLabel}>{t('skills_detail_industries_label')}</span>
          <p>{formatSectionValue(detail.industries)}</p>
        </section>
      </div>

      {shouldShowVideo ? (
        <section className={s.detailSection}>
          <span className={s.detailLabel}>{t('skills_detail_video_label')}</span>
          <div className={s.videoSection}>
            <video
              className={s.videoPlayer}
              preload='metadata'
              src={detail.screencastVideoLink}
              controls
            >
              {t('skills_detail_video_not_supported')}
            </video>
          </div>
        </section>
      ) : null}

      {promptItems.length > 0 ? (
        <section className={s.detailSection}>
          <span className={s.detailLabel}>{t('skills_detail_example_prompts_label')}</span>
          <ol className={s.promptList}>
            {promptItems.map((prompt, index) => {
              const promptKey = `prompt-${index + 1}-${prompt.slice(0, 24)}`;
              const parsedPrompt = splitPromptExample(prompt);

              return (
                <li className={s.promptListItem} key={promptKey}>
                  {parsedPrompt.prompt || '—'}
                </li>
              );
            })}
          </ol>
        </section>
      ) : null}

      <section className={s.detailSection}>
        <span className={s.detailLabel}>Company</span>
        <p>{detailCompany || '—'}</p>
      </section>

      <section className={s.detailSection}>
        <span className={s.detailLabel}>{t('skills_detail_contact_label')}</span>
        {detailContact ? (
          <a className={s.inlineLink} href={`mailto:${detailContact}`}>
            {detailContact}
          </a>
        ) : (
          <p>—</p>
        )}
      </section>

      {detail.legalDisclaimer ? (
        <section className={s.detailSection}>
          <span className={s.detailLabel}>{t('skills_detail_legal_label')}</span>
          <a
            className={s.inlineLink}
            href={detail.legalDisclaimer}
            rel='noreferrer'
            target='_blank'
          >
            {t('skills_detail_disclaimer_link')}
          </a>
        </section>
      ) : null}

      {detail.authorizationRequirements ? (
        <section className={s.detailSection}>
          <span className={s.detailLabel}>{t('skills_detail_authorization_label')}</span>
          <p>{detail.authorizationRequirements}</p>
        </section>
      ) : null}

      <section className={s.detailSection}>
        <span className={s.detailLabel}>{t('skills_detail_countries_label')}</span>
        <p>{formatSectionValue(detail.countries)}</p>
      </section>

      <DownloadAcknowledge
        isLoggedIn={isLoggedIn}
        isTermsAccepted={isTermsAccepted}
        onTermsChange={onTermsChange}
      />
    </div>
  );
}

function VersionHistoryTab({ detail }: { detail: SkillDetailItem | null }) {
  const t = useTranslations();

  if (!detail) {
    return <div className={s.detailEmptyState}>{t('skills_detail_unavailable')}</div>;
  }

  const versionHistory = detail.versionHistory || [];

  if (versionHistory.length < 1) {
    return <div className={s.detailEmptyState}>{t('skills_detail_version_history_empty')}</div>;
  }

  return (
    <div className={s.versionHistoryList}>
      {versionHistory.map((version) => (
        <div
          className={s.versionHistoryItem}
          key={`${version.versionSemver}-${version.publishDate}`}
        >
          <div className={s.versionHistoryTitleRow}>
            <h4>{t('skills_detail_version_title', { version: version.versionSemver })}</h4>
            {version.isActive ? (
              <span className={s.versionHistoryBadge}>{t('skills_detail_active')}</span>
            ) : null}
          </div>
          <p>
            <strong>{t('skills_detail_publish_date_label')}</strong>{' '}
            {formatVersionDate(version.publishDate)}
          </p>
          <p>
            <strong>{t('skills_detail_publish_notes_label')}</strong> {version.publishNotes || '—'}
          </p>
        </div>
      ))}
    </div>
  );
}

export function SkillDetailOverlay({
  detail,
  fallbackSkill,
  isBannerVisible,
  isLoggedIn,
  onBannerDismiss,
  onClose,
}: {
  detail: SkillDetailItem | null;
  fallbackSkill: SkillItem | null;
  isBannerVisible: boolean;
  isLoggedIn: boolean;
  onBannerDismiss: () => void;
  onClose: () => void;
}) {
  const t = useTranslations();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [activeTab, setActiveTab] = useState<DetailTabKey>('details');
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  useEffect(() => {
    setActiveTab('details');
    setIsTermsAccepted(false);
    setIsDownloading(false);
    setIsInstalling(false);
  }, [detail?.skillId, fallbackSkill?.id]);

  const currentUrl = useMemo(() => {
    const currUrl = new URL(`${BASE_PATH}${pathname}`);
    currUrl.search = searchParams?.toString() || '';
    return currUrl.href;
  }, [pathname, searchParams]);

  const handleDownload = async () => {
    if (!detail?.skillId) {
      message.error(t('skills_download_modal_error'));
      return;
    }

    if (!isTermsAccepted) {
      return;
    }

    setIsDownloading(true);

    try {
      const skillHubReferer = `${BASE_PATH}/agentic-hub`;
      const response = await ky.get(APIs.aiSkillHub.skillDownloadGet, {
        headers: {
          referer: skillHubReferer,
        },
        referrer: skillHubReferer,
        searchParams: {
          skill_id: detail.skillId,
          source: 2,
        },
      });

      const logId = response.headers.get('x-tt-logid') || '';
      const rawResponse = await response.json<MAxiosResponse<AiSkillHubSkillDownloadData>>();
      const downloadData = mapAiSkillHubSkillDownload(rawResponse.data);

      console.log('[agentic-hub] browser download response', rawResponse.data);
      console.log('[agentic-hub] browser download x-tt-logid', logId);

      if (!downloadData?.downloadUrl) {
        throw new Error('missing download url');
      }

      const normalizedDownloadUrl = normalizeCdnUrl(downloadData.downloadUrl);
      if (!normalizedDownloadUrl) {
        throw new Error('invalid download url');
      }

      message.success(t('skills_download_modal_success'));
      window.open(normalizedDownloadUrl, '_self', 'noopener,noreferrer');
    } catch (error) {
      console.log('[agentic-hub] browser download failed', error);
      message.error(t('skills_download_modal_error'));
    } finally {
      setIsDownloading(false);
    }
  };

  const handleInstall = async () => {
    if (!detail?.skillId) {
      message.error('Unable to install this skill right now.');
      return;
    }

    if (!isTermsAccepted) {
      return;
    }

    setIsInstalling(true);

    try {
      const skillHubReferer = `${BASE_PATH}/agentic-hub`;
      const response = await ky.get(APIs.aiSkillHub.skillInstall, {
        headers: {
          referer: skillHubReferer,
        },
        referrer: skillHubReferer,
        searchParams: {
          skill_id: detail.skillId,
        },
      });

      const logId = response.headers.get('x-tt-logid') || '';
      const rawResponse = await response.json<MAxiosResponse<Record<string, never>>>();

      console.log('[agentic-hub] browser install response', rawResponse.data);
      console.log('[agentic-hub] browser install x-tt-logid', logId);

      message.success(
        'Installing the skill to your Ad Assistant now, you will be redirected soon...'
      );

      window.setTimeout(() => {
        const normalizedSkillId = detail.skillId.replace(/^SKL_/, '');
        const copilotHomeUrl = new URL('https://ads.tiktok.com/i18n/home');
        copilotHomeUrl.searchParams.set('open_copilot', '1');
        copilotHomeUrl.searchParams.set('skillId', normalizedSkillId);

        window.open(copilotHomeUrl.toString(), '_blank', 'noopener,noreferrer');
      }, 2000);
    } catch (error) {
      console.log('[agentic-hub] browser install failed', error);
      message.error('Unable to install this skill right now.');
    } finally {
      setIsInstalling(false);
    }
  };

  return (
    <div className={s.overlay} onClick={onClose}>
      <div className={s.overlayShell} onClick={(event) => event.stopPropagation()}>
        <button
          aria-label={t('skills_detail_close')}
          className={s.sideCloseButton}
          type='button'
          onClick={onClose}
        >
          <svg fill='none' height='16' viewBox='0 0 16 16' width='16'>
            <path
              clipRule='evenodd'
              d='M2.46967 2.46967C2.76256 2.17678 3.23744 2.17678 3.53033 2.46967L13.5303 12.4697C13.8232 12.7626 13.8232 13.2374 13.5303 13.5303C13.2374 13.8232 12.7626 13.8232 12.4697 13.5303L2.46967 3.53033C2.17678 3.23744 2.17678 2.76256 2.46967 2.46967Z'
              fill='white'
              fillRule='evenodd'
            />
            <path
              clipRule='evenodd'
              d='M2.46967 13.5303C2.17678 13.2374 2.17678 12.7626 2.46967 12.4697L12.4697 2.46967C12.7626 2.17678 13.2374 2.17678 13.5303 2.46967C13.8232 2.76256 13.8232 3.23744 13.5303 3.53033L3.53033 13.5303C3.23744 13.8232 2.76256 13.8232 2.46967 13.5303Z'
              fill='white'
              fillRule='evenodd'
            />
          </svg>
        </button>
        <aside
          aria-labelledby='skill-detail-title'
          aria-modal='true'
          className={s.overlayPanel}
          role='dialog'
        >
          <div className={s.overlayHeader}>
            <h2>{t('skills_detail_title')}</h2>
            <button
              aria-label={t('skills_detail_close')}
              className={s.headerCancelButton}
              type='button'
              onClick={onClose}
            >
              {t('skills_download_modal_cancel')}
            </button>
          </div>

          <div className={s.overlayTabs}>
            <DetailTabs activeTab={activeTab} onChange={setActiveTab} />
          </div>

          <div className={s.overlayBody}>
            {isBannerVisible ? (
              <div className={s.overlayWarningBannerWrap}>
                <McpInstallWarningBanner variant='overlay' onDismiss={onBannerDismiss} />
              </div>
            ) : null}

            {activeTab === 'details' ? (
              <DetailsTab
                detail={detail}
                fallbackSkill={fallbackSkill}
                isLoggedIn={isLoggedIn}
                isTermsAccepted={isTermsAccepted}
                onTermsChange={setIsTermsAccepted}
              />
            ) : (
              <VersionHistoryTab detail={detail} />
            )}
          </div>

          <div className={s.overlayFooter}>
            <FooterActions
              currentUrl={currentUrl}
              detail={detail}
              isDownloading={isDownloading}
              isInstalling={isInstalling}
              isLoggedIn={isLoggedIn}
              isTermsAccepted={isTermsAccepted}
              onDownload={handleDownload}
              onInstall={handleInstall}
            />
          </div>
        </aside>
      </div>
    </div>
  );
}
